// Vitality Score detail screen (postmenopause only). Mirrors the layout
// shape of MenopauseScoreDetail — back link → gauge card with headline +
// band pill + lead + recent-average → 14-day chart → "What's driving it"
// → methodology footer — so the two detail pages stay visually paired.
//
// Recovery math is delegated to vitalityScore.ts: the headline + driver
// contributions come from `computeVitalityScore` (today only), the line
// chart + 7-day average come from `computeVitalityScoreSeries` (14d). Both
// run on the SAME wearable rows fetched once here. Peri/meno is untouched
// — this screen is only rendered when stage === "Postmenopause".

import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  LineChart, Line, LabelList, ResponsiveContainer, XAxis, YAxis, Tooltip,
} from "recharts";
import {
  computeVitalityScore,
  computeVitalityScoreSeries,
  vitalityBandFor,
  type VitalityScore,
  type VitalityScoreSeriesPoint,
} from "./vitalityScore";
import { fetchWearableMetrics, type WearableMetricRow } from "./wearableMetrics";
import { BASELINE_WINDOW_NIGHTS } from "./symptomRiskForecast";
import { parseLocalDateKey } from "./dailyLogs";

const GR = "#6E4B71", GL = "#FAF7F5", GD = "#6E4B71";
const WH = "#ffffff";

function fmtShort(dk: string): string {
  const d = parseLocalDateKey(dk); if (!d) return dk;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function deltaWord(lastNight: number, baseline: number, label: string): string {
  const delta = lastNight - baseline;
  if (Math.abs(delta) < 0.5) return `near your ${label} baseline`;
  return delta > 0 ? `above your ${label} baseline` : `below your ${label} baseline`;
}

export function VitalityScoreDetail({
  user: _user,
  onBack,
}: {
  user: any;
  onBack: () => void;
}) {
  const [rows, setRows] = useState<WearableMetricRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getUser();
        const uid = sess?.user?.id;
        if (!uid) { if (!cancelled) setLoading(false); return; }
        // Fetch enough to give the OLDEST point in a 14d series a full
        // BASELINE_WINDOW_NIGHTS of prior rows for its baseline calc.
        const r = await fetchWearableMetrics(uid, 14 + BASELINE_WINDOW_NIGHTS + 2);
        if (!cancelled) { setRows(r); setLoading(false); }
      } catch (e) {
        console.warn("[VitalityScoreDetail] fetch failed", e);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const today: VitalityScore | null = useMemo(
    () => (rows ? computeVitalityScore(rows) : null),
    [rows],
  );
  const series: VitalityScoreSeriesPoint[] = useMemo(
    () => (rows ? computeVitalityScoreSeries(rows, 14) : []),
    [rows],
  );

  if (loading) {
    return <div style={{ padding: 20, color: GR, fontSize: 13 }}>Loading your Vitality Score…</div>;
  }

  const isOk = today?.status === "ok" && today.score != null && today.band;
  const todayScore = isOk ? (today!.score as number) : null;
  const band = todayScore != null ? vitalityBandFor(todayScore) : null;

  // Recent average — last 7 series points where score != null. Honest gate:
  // need ≥3 eligible days, mirroring MenopauseScoreDetail's smoothing rule.
  const recent7 = series.slice(-7).filter(p => p.score != null) as { score: number }[];
  const recentAvg = recent7.length >= 3
    ? Math.round(recent7.reduce((a, b) => a + b.score, 0) / recent7.length)
    : null;
  const recentBand = recentAvg != null ? vitalityBandFor(recentAvg) : null;

  // 14-day chart rows with trailing 7-day smoothed overlay computed at each
  // point (eligible-only) — same chart vocabulary as MenopauseScoreDetail.
  const last14 = series.map((p, i) => {
    const start = Math.max(0, i - 6);
    const win = series.slice(start, i + 1).filter(d => d.score != null) as { score: number }[];
    const smoothedAt = win.length >= 3
      ? Math.round(win.reduce((a, b) => a + b.score, 0) / win.length)
      : null;
    return { dateKey: p.dateKey, label: fmtShort(p.dateKey), score: p.score, smoothed: smoothedAt };
  });

  // Gauge geometry — IDENTICAL to MenopauseScoreDetail (gR=80, gCx=100,
  // gCy=100, 200x120 viewBox) so the two detail pages render the same arc.
  const gR = 80, gCx = 100, gCy = 100;
  const gAngle = todayScore == null ? 0 : (todayScore / 100) * Math.PI;
  const gx = gCx - gR * Math.cos(gAngle);
  const gy = gCy - gR * Math.sin(gAngle);
  const arcPath = todayScore == null
    ? ""
    : `M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 ${gAngle > Math.PI ? 1 : 0} 1 ${gx.toFixed(1)} ${gy.toFixed(1)}`;

  const arcColor = band?.color ?? "#E8D5DA";

  return (
    <div style={{ paddingBottom: 24 }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: GR, fontSize: 13, cursor: "pointer", marginBottom: 10, padding: "4px 0" }}>
        ← Back
      </button>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: "#FF5C7A", margin: "0 0 4px", letterSpacing: "-0.01em" }}>Vitality Score</h1>
      <div style={{ fontSize: 12, color: GR, marginBottom: 16 }}>How well you're recovering, night by night.</div>

      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: 18, marginBottom: 14, textAlign: "center" }}>
        <svg width={200} height={120} viewBox="0 0 200 120">
          <path d={`M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 1 1 ${gCx + gR} ${gCy}`} fill="none" stroke={GL} strokeWidth={10} strokeLinecap="round" />
          {arcPath && <path d={arcPath} fill="none" stroke={arcColor} strokeWidth={10} strokeLinecap="round" />}
          <text x={gCx} y={gCy - 6} textAnchor="middle" fontSize="44" fontWeight="600" fill={GD}>{todayScore ?? "—"}</text>
          <text x={gCx} y={gCy + 14} textAnchor="middle" fontSize="11" fill={GR}>of 100 · today</text>
        </svg>
        {band && (
          <div>
            <span style={{ display: "inline-block", padding: "5px 14px", borderRadius: 20, background: band.color + "22", color: band.color, fontSize: 12, fontWeight: 500 }}>{band.label}</span>
          </div>
        )}
        <div style={{ fontSize: 13, color: GD, lineHeight: 1.5, marginTop: 12, padding: "10px 12px", background: "#FAF7F5", borderRadius: 10 }}>
          {today?.lead ?? "Your Vitality Score is calibrating."}
        </div>

        {recentAvg != null && recentBand && (
          <div style={{ marginTop: 10, fontSize: 12, color: GD, padding: "8px 10px", background: "#FFFFFF", border: "0.5px solid #E8D5DA", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ color: GR }}>Recent average (last 7 days)</span>
            <span style={{ fontWeight: 600, color: GD }}>
              {recentAvg}
              <span style={{ marginLeft: 6, padding: "2px 8px", borderRadius: 12, background: recentBand.color + "22", color: recentBand.color, fontSize: 11, fontWeight: 500 }}>{recentBand.label}</span>
            </span>
          </div>
        )}
      </div>

      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: GD, marginBottom: 4 }}>Last 14 days</div>
        <div style={{ fontSize: 11, color: GR, marginBottom: 10 }}>Today is highlighted. Missing days = no wearable data.</div>
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <LineChart data={last14} margin={{ top: 20, right: 14, left: -16, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: GR }} interval={1} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: GR }} ticks={[0, 50, 100]} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "0.5px solid #E8D5DA" }}
                content={(props: any) => {
                  const { active, payload, label } = props;
                  if (!active || !payload || !payload.length) return null;
                  const first = payload.find((p: any) => p && p.value != null);
                  const val = first ? first.value : null;
                  return (
                    <div style={{ background: WH, fontSize: 11, borderRadius: 8, border: "0.5px solid #E8D5DA", padding: "6px 10px" }}>
                      <div style={{ color: GR, marginBottom: 2 }}>{label}</div>
                      <div style={{ color: GD }}>Score: {val == null ? "—" : val}</div>
                    </div>
                  );
                }}
              />
              {/* Dashed connector underlay — same as MenopauseScoreDetail. */}
              <Line type="monotone" dataKey="score" stroke={GD} strokeOpacity={0.5} strokeWidth={1.5}
                    strokeDasharray="3 4" dot={false} activeDot={false} isAnimationActive={false}
                    connectNulls={true} legendType="none" />
              <Line type="monotone" dataKey="score" stroke={arcColor} strokeWidth={2} dot={(props: any) => {
                const { cx, cy, index, payload } = props;
                if (payload?.score == null) return <g />;
                const isToday = index === last14.length - 1;
                return <circle key={index} cx={cx} cy={cy} r={isToday ? 5 : 3} fill={isToday ? arcColor : WH} stroke={arcColor} strokeWidth={isToday ? 2 : 1.5} />;
              }} connectNulls={false}>
                <LabelList dataKey="score" position="top" offset={8}
                  content={(props: any) => {
                    const { x, y, value, index } = props;
                    if (value == null) return null;
                    const n = last14.length;
                    const anchor = index === 0 ? "start" : index === n - 1 ? "end" : "middle";
                    return <text x={x} y={y - 8} fontSize={9} fontWeight={500} fill={GD} textAnchor={anchor}>{Math.round(value)}</text>;
                  }}
                />
              </Line>
              {/* Trailing 7-day smoothed overlay — faint, no dots. */}
              <Line type="monotone" dataKey="smoothed" stroke={GD} strokeOpacity={0.35}
                    strokeWidth={1.5} dot={false} activeDot={false}
                    isAnimationActive={false} connectNulls={true} legendType="none" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: GD, marginBottom: 4 }}>What's driving it</div>
        <div style={{ fontSize: 11, color: GR, marginBottom: 12 }}>Each signal compared to your personal baseline.</div>
        {!today || today.contributions.length === 0 ? (
          <div style={{ fontSize: 12, color: GR, padding: "12px 0", textAlign: "center" }}>
            {today?.status === "building_baseline"
              ? "Building your baseline — driver breakdown will appear once enough nights are logged."
              : "No wearable signals available yet."}
          </div>
        ) : today.contributions.map(c => {
          const word = deltaWord(c.lastNight, c.baseline, c.label.toLowerCase());
          const dirMeta =
            c.direction === "better" ? { text: "↑ Helping",   color: "#6E4B71" } :
            c.direction === "worse"  ? { text: "↓ Dragging",  color: "#FF5C7A" } :
                                         { text: "→ Steady",    color: "#6E4B71" };
          return (
            <div key={c.key} style={{ borderTop: "0.5px solid #FAF7F5", padding: "10px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: GD, fontWeight: 500 }}>{c.label}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: dirMeta.color, background: dirMeta.color + "22", padding: "2px 8px", borderRadius: 12 }}>
                  {dirMeta.text}
                </span>
              </div>
              <div style={{ fontSize: 11.5, color: GR }}>{c.detail} · {word}</div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: GR, lineHeight: 1.55, padding: "0 4px" }}>
        <strong style={{ fontWeight: 500, color: GD }}>How it's calculated:</strong> Your Vitality Score compares your overnight recovery to your own personal baseline. Higher = better recovered.
      </div>
    </div>
  );
}
